import { ColumnType } from "antd/es/table";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { onSortAntd } from "components/atoms/table/AntdTable";
import Tabs from "components/atoms/tabs/Tabs";
import Status, { StatusType } from "components/blocks/status/Status";
import { TableTemplate } from "components/templates/default/table/TableTemplate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useMenu } from "hook/useMenu";
import styled from "styled-components";
import { TitleComponentType } from "components/blocks/subheader/SubHeader";

export enum STAGING_TABS {
  RECORDER = "/maintenance/staging",
  HISTORY = "/maintenance/staging/history",
}

export interface StagingPageOutletContext {
  updateQueryInfo: (partial: Partial<BasePagination>) => void;
  onHeaderCell: (column: ColumnType<any>) => { onClick: () => void };
  onChangePage: (page: number, totalRows?: number) => void;
  setStatus: React.Dispatch<React.SetStateAction<StatusType>>;
  setButtons: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  onClickFilterTitle: (value: string | undefined, name: string) => void;
  status: StatusType & { autoUpdate?: boolean; cloudStatus?: boolean };
  onClickSearch: (keyword: string) => void;
  setCenterChildren: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  setClassName: React.Dispatch<React.SetStateAction<string>>;
  setNoSearch: React.Dispatch<React.SetStateAction<boolean>>;
  setTitleComponent: React.Dispatch<React.SetStateAction<TitleComponentType>>;
}

export default function StagingPage(): JSX.Element {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [centerChildren, setCenterChildren] = useState<React.ReactNode>(null);
  const [className, setClassName] = useState<string>("");
  const [noSearch, setNoSearch] = useState<boolean>(false);

  const keyword = useMemo(
    () => searchParams.get("keyword") ?? "",
    [searchParams]
  );
  // queryInfo를 바꾸고 싶을 때는 이 함수를 통해 searchParams만 업데이트
  const updateQueryInfo = useCallback(
    (partial: Partial<BasePagination>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(partial).forEach(([key, value]) => {
          if (
            key === "pageNum" &&
            prev.get(key) === null &&
            Number(value) === 0
          ) {
            next.delete(key);
            return;
          }

          if (value === null || value === undefined || value === "") {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        });
        return next;
      });
    },
    [setSearchParams]
  );
  const onClickSearch = useCallback(
    (keyword: string) => {
      updateQueryInfo({
        keyword: keyword,
        pageNum: 0,
      });
    },
    [updateQueryInfo]
  );
  const onHeaderCell = (column: ColumnType<any>) => ({
    onClick: () => {
      if (column.key) {
        updateQueryInfo({
          sortType: column.key as string,
          sortDirection: onSortAntd(
            (searchParams.get("sortDirection") as SORT_DIRECTION) ??
              SORT_DIRECTION.ASC
          ),
        });
      }
    },
  });
  const onChangePage = (page: number, totalRows?: number) => {
    updateQueryInfo({ pageNum: page - 1 });
  };
  const [status, setStatus] = useState<StatusType>({
    totalTitle: "Total",
    totalValue: 0,
  });
  const [buttons, setButtons] = useState<React.ReactNode>(null);
  const onClickFilterTitle = useCallback(
    (value: string | undefined, name: string) => {
      if (value !== undefined) {
        updateQueryInfo({
          pageNum: 0,
          [name]: value === "all" ? null : value,
        });
      }
    },
    [updateQueryInfo]
  );
  const [titleComponent, setTitleComponent] =
    useState<TitleComponentType>(null);
  const outletContext: StagingPageOutletContext = {
    updateQueryInfo,
    onHeaderCell,
    onChangePage,
    setStatus,
    setButtons,
    onClickFilterTitle,
    status,
    onClickSearch,
    setCenterChildren,
    setClassName,
    setNoSearch,
    setTitleComponent,
  };
  useEffect(() => {
    if (!location.pathname.includes("/account")) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("keyword");
      next.delete("pageNum");
      next.delete("pageLimit");
      next.delete("sortType");
      next.delete("sortDirection");
      next.delete("type");
      next.delete("fromDate");
      next.delete("toDate");
      next.delete("status");
      return next;
    });
  }, [location.pathname]);
  return (
    <Wrapper className={location.pathname.replaceAll("/", "_")}>
      <TableTemplate
        titleComponent={titleComponent}
        centerChildren={centerChildren}
        keyword={keyword}
        onClickSearch={onClickSearch}
        buttons={buttons}
        noSearch={noSearch}
        className={className}
      >
        <Outlet context={outletContext} />
      </TableTemplate>
    </Wrapper>
  );
}
const Wrapper = styled.div`
  &._manager_staging_assign .bg-container .table-template-content {
    ${({ theme }) =>
      theme.value !== "light" &&
      `
    background-color: ${theme.COLORS.PRIMARY3};
  `}
  }
`;
